import React, { useEffect, useState } from 'react';
import { Col, Row, Card, Table } from 'antd';
import DetailBar from '../../components/DetailBar';
import ConversionLineChart from '../../components/ConversionsLineChart';
import ConversionBarChart from '../../components/ConversionsBarChart';
import { KPITitleText, KpiSubText } from '../../../../../shared/globalStyling/styledText';
import { useSEOConversionsContext } from '../../../../../../core/components/seoRefactored/seoDetails/context/SEOConversionsContext';
import { LoadingPage } from '../../../../../shared/LoadingPage';
import { formatWholeNumber } from '../../../../../../core/utils/campaigns';
import moment from 'moment';
import NewDetailBar from '../../components/NewDetailBar';
import RevenueByDateLineChart from '../../components/RevenueByDateLineChart';

const ConversionsView = ({ selectedDate, setSelectedDate, setReportData }) => {
  const {
    conversionsSummary,
    conversionsByDate,
    reportLoading,
    conversionsBySource,
    secondsToFormatted,
    startDateEnDate,
    onDateChange,
    revenueByTrafficSummary,
    revenueByTraffic,
    mediums,
  } = useSEOConversionsContext();
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (
      selectedDate &&
      selectedDate.length > 0 &&
      startDateEnDate.length > 0 &&
      (!selectedDate[0].isSame(startDateEnDate[0]) ||
        !selectedDate[1].isSame(startDateEnDate[1])) &&
      !reportLoading
    ) {
      onDateChange(selectedDate);
    }
  }, [onDateChange, reportLoading, selectedDate, startDateEnDate]);

  useEffect(() => {
    if (!reportLoading && conversionsByDate && conversionsByDate.length > 0 && initialLoad) {
      const startDate = conversionsByDate[0].date;
      const endDate = conversionsByDate[conversionsByDate.length - 1].date;
      setSelectedDate([moment(startDate), moment(endDate)]);
      if (conversionsBySource.length > 0) {
        setReportData(
          conversionsBySource.map((item) => ({
            'Traffic Source': item.trafficSource,
            Conversions: item.conversions,
            'New Users': item.newUsers,
            'Total Users': item.totalUsers,
            Engagement: item.userEngagement,
          }))
        );
      }
      setInitialLoad(false);
    }
  }, [
    conversionsByDate,
    conversionsBySource,
    initialLoad,
    reportLoading,
    setReportData,
    setSelectedDate,
  ]);

  useEffect(() => {
    if (conversionsByDate && conversionsByDate.length > 0 && !initialLoad) {
      setReportData(
        conversionsBySource.map((item) => ({
          'Traffic Source': item.trafficSource,
          Conversions: item.conversions,
          'New Users': item.newUsers,
          'Total Users': item.totalUsers,
          Engagement: item.userEngagement,
        }))
      );
    }
  }, [conversionsByDate, conversionsBySource, initialLoad, setReportData]);

  if (reportLoading) return <LoadingPage />;

  const cols2 = {
    conversions: {
      min: 0,
      range: [0, 0.93],
    },
    date: {
      range: [0, 0.9],
    },
  };

  const columns = [
    {
      title: 'Traffic Source',
      dataIndex: 'trafficSource',
      key: 'trafficSource',
    },
    {
      title: 'Conversions',
      dataIndex: 'conversions',
      key: 'conversions',
      render: (text) => formatWholeNumber(text),
    },
    {
      title: 'New Users',
      dataIndex: 'newUsers',
      key: 'newUsers',
      render: (text) => formatWholeNumber(text),
    },
    {
      title: 'Total Users',
      dataIndex: 'totalUsers',
      key: 'totalUsers',
      render: (text) => formatWholeNumber(text),
    },
    {
      title: 'Engagement',
      dataIndex: 'userEngagement',
      key: 'userEngagement',
    },
  ];

  const totalConversions = conversionsSummary?.values?.[0]?.value || 0;

  return (
    <React.Fragment>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <DetailBar dataToDisplay={conversionsSummary} />
        </Col>
        <Col span={12}>
          <Card title={'CONVERSIONS'}>
            <div style={{ marginBottom: '20px' }}>
              <KPITitleText text={'Conversions'} />
              <KpiSubText text={totalConversions} />
            </div>
            <ConversionLineChart data={conversionsByDate} scale={cols2} />
          </Card>
        </Col>
        <Col span={12}>
          <Card title={'CONVERSIONS BY SOURCE'}>
            <div style={{ marginBottom: '20px' }}>
              <KPITitleText text={'Conversions'} />
              <KpiSubText text={totalConversions} />
            </div>
            <ConversionBarChart data={conversionsBySource} />
          </Card>
        </Col>
        <Col span={24}>
          <Card title={'TOTAL CONVERSIONS BY TRAFFIC SOURCES'}>
            <Table
              style={{ width: '100%' }}
              columns={columns}
              dataSource={conversionsBySource}
              size={'default'}
              scroll={{ x: 300 }}
              summary={(record) => {
                const totalSummary = {
                  conversions: 0,
                  newUsers: 0,
                  totalUsers: 0,
                  userEngagementSeconds: 0,
                };

                record.forEach((item) => {
                  totalSummary.conversions += item.conversions;
                  totalSummary.newUsers += item.newUsers;
                  totalSummary.totalUsers += item.totalUsers;
                  totalSummary.userEngagementSeconds += item.userEngagementSeconds;
                });
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>Total:</Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        {formatWholeNumber(totalSummary.conversions)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>
                        {formatWholeNumber(totalSummary.newUsers)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        {formatWholeNumber(totalSummary.totalUsers)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>
                        {`Avg: ` +
                          secondsToFormatted(
                            record.length > 0
                              ? totalSummary.userEngagementSeconds / record.length
                              : 0
                          )}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Card>
        </Col>
        <Col xs={24}>
          <Card title={'REVENUE BY SOURCE'}>
            <Row gutter={[12, 12]}>
              <Col xs={24}>
                <NewDetailBar dataToDisplay={revenueByTrafficSummary} title={'Total Revenue'} />
              </Col>
              <Col xs={24}>
                <RevenueByDateLineChart data={revenueByTraffic} mediums={mediums} />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default ConversionsView;
